// Avoid having an export on the first line for istanbul

import { useState } from 'react';
import axios from 'axios';

export const API_V3 = '/api/v3';
export const API_V2 = '/api/v2';
export const PRIVATE_API = '/api/private';

export const DEFAULT_PAGE_SIZE = 100;

export interface ApiV3Response<T> {
  data: T;
  status: {
    code: number;
    developer_message: string;
    is_success: boolean;
    user_message: string;
  };
  extra?: {
    filters: { [any: string]: any };
    order_by: string;
    pagination: {
      count: number;
      total_count: number;
    };
  };
}

export interface ApiV2Response<T> {
  data: T;
  status: {
    code: number;
    developer_message: string;
    is_success: boolean;
    user_message: string;
  };
  extra?: {
    filters: { [any: string]: any };
    order_by: string;
    pagination: {
      count: number;
      total_count: number;
    };
  };
}

export interface PrivateApiResponse<T> {
  data: T;
  status: {
    code: number;
    developer_message: string;
    is_success: boolean;
    user_message: string;
  };
}

export type PrivateApiErrorData = Record<string, any>;

export interface Job {
  id: number;
  name: string;
}

export interface Environment {
  id?: number;
  name?: string;
  jobs?: Job[];
  project_id?: number;
  deployment_type?: string;
  enable_model_query_history?: boolean;
  type?: string;
}

export type EnvironmentType = 'development' | 'deployment';
export type DeploymentType = 'production' | 'staging';
export type DefaultEnvironmentType = DeploymentType | 'development';

export const allDefaultEnvironmentTypes: DefaultEnvironmentType[] = [
  'production',
  'staging',
  'development',
];

/** Returns the environment ids for the specified environment types for a project */
export function getDefaultEnvironmentIds(
  environments: Environment[],
  /** The default environment types to include. If not provided, only production environments will be included. */
  envTypes: DefaultEnvironmentType[] = ['production'],
): number[] {
  const filteredEnvironments = environments.filter((environment) => {
    if (environment.type === 'development') {
      return envTypes.includes('development');
    }
    return envTypes.includes(environment.deployment_type as DefaultEnvironmentType);
  });

  return filteredEnvironments
    .map((environment) => environment.id)
    .filter((id): id is number => id !== undefined);
}

export const getSomethingPaginated = async <T>(
  api_endpoint: typeof API_V3 | typeof API_V2,
  endpoint: string, // looks like `/accounts/${accountId}/environments/`
  params: any,
): Promise<T[]> => {
  const fullEndpoint = `${api_endpoint}/${endpoint.startsWith('/') ? endpoint.substring(1) : endpoint}`;
  let isTruncated = true;
  let incrementalCount = 0;
  let response: any;
  const resultArray: T[] = [];
  while (isTruncated) {
    response = await axios.get<ApiV3Response<T[]>>(fullEndpoint, {
      params: {
        ...params,
        limit: DEFAULT_PAGE_SIZE,
        offset: incrementalCount,
      },
    });
    if (!response || response?.data?.status?.code !== 200) return [];
    incrementalCount += response?.data?.extra?.pagination?.count || 0;
    isTruncated =
      incrementalCount < (response?.data?.extra?.pagination?.total_count || 0);
    resultArray.push(...response?.data?.data);
  }
  return resultArray;
};

export const useSomethingLazily = () => {
  const [data, setData] = useState<any>(null);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<any>(null);

  const queryExecute = async (args: any) => {
    setFetching(true);
    try {
      const fullEndpoint = `${args.api_endpoint}/${args.endpoint.startsWith('/') ? args.endpoint.substring(1) : args.endpoint}`;
      const response = await axios.get<ApiV3Response<any>>(fullEndpoint, {
        params: {
          ...args.params,
          limit: args.first || DEFAULT_PAGE_SIZE,
          offset: args.after ? parseInt(args.after, 10) : 0,
        },
      });

      if (!response || response?.data?.status?.code !== 200) {
        setError(new Error('Failed to fetch data'));
        return {
          data: null,
          error: new Error('Failed to fetch data'),
          fetching: false,
        };
      }

      setData(response.data);
      setFetching(false);
      return {
        data: response.data,
        error: null,
        fetching: false,
        pageInfo: {
          hasNextPage:
            (response.data.extra?.pagination?.count || 0) +
              (args.after ? parseInt(args.after, 10) : 0) <
            (response.data.extra?.pagination?.total_count || 0),
          endCursor: (
            (args.after ? parseInt(args.after, 10) : 0) +
            (response.data.extra?.pagination?.count || 0)
          ).toString(),
          totalCount: response.data.extra?.pagination?.total_count || 0,
        },
      };
    } catch (err) {
      setError(err);
      setFetching(false);
      return { data: null, error: err, fetching: false };
    }
  };

  return [{ data, fetching, error }, queryExecute] as const;
};
